import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { format, subMonths, subYears, subDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns"
import { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface DateRangePickerProps {
  dateRange?: DateRange
  onDateRangeChange?: (range: DateRange | undefined) => void
  placeholder?: string
  className?: string
}

const presetRanges = [
  {
    label: "Last 7 days",
    getRange: () => ({
      from: subDays(new Date(), 7),
      to: new Date()
    })
  },
  {
    label: "Last month",
    getRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth)
      }
    }
  },
  {
    label: "Last 3 months", 
    getRange: () => ({
      from: subMonths(new Date(), 3),
      to: new Date()
    })
  },
  {
    label: "Last 6 months",
    getRange: () => ({
      from: subMonths(new Date(), 6),
      to: new Date()
    })
  },
  {
    label: "Last year",
    getRange: () => {
      const lastYear = subYears(new Date(), 1);
      return {
        from: startOfYear(lastYear),
        to: endOfYear(lastYear)
      }
    }
  },
  {
    label: "This year",
    getRange: () => ({
      from: startOfYear(new Date()),
      to: new Date()
    })
  }
];

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  placeholder = "Pick a date range",
  className
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const handlePresetSelect = (preset: typeof presetRanges[0]) => {
    const range = preset.getRange();
    onDateRangeChange?.(range);
    setIsOpen(false);
  }

  const handleCalendarSelect = (range: DateRange | undefined) => {
    onDateRangeChange?.(range);
    // Don't auto-close to allow users to freely select dates
    // Only close when both dates are selected and user clicks outside or presses escape
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "justify-start text-left font-normal",
              !dateRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "LLL dd, y")} -{" "}
                  {format(dateRange.to, "LLL dd, y")}
                </>
              ) : (
                format(dateRange.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-lg border-0" align="start">
          <div className="flex">
            {/* Preset options */}
            <div className="flex flex-col border-r border-border p-4 space-y-1 min-w-[160px] bg-muted/30">
              <div className="text-sm font-semibold text-foreground mb-3">Quick select</div>
              {presetRanges.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-left font-normal h-9 px-3 hover:bg-primary/10 hover:text-primary transition-colors"
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            {/* Calendar */}
            <div className="p-4">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={handleCalendarSelect}
                numberOfMonths={2}
                className="pointer-events-auto"
              />
              {dateRange?.from && dateRange?.to && (
                <div className="mt-4 pt-3 border-t border-border">
                  <Button 
                    onClick={() => setIsOpen(false)}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                  >
                    Apply Selection
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}